/**
 * Homepage: six-sport row + five product layers (doc 23 §2-§3, §9).
 *
 * Overview by default; tapping a layer expands it in place while the other
 * four dim and collapse to their title row, so the visitor always sees where
 * they are in the five. No required order, no auto-play.
 *
 * State: activeSport (drives example content) + zoomedLayer (null = overview).
 * All copy lives in layerContent.js / sportsData.js.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { SPORTS } from './sportsData';
import { LAYERS, layerDetail, demoBeats, TITLE_MENU_MS } from './layerContent';
import { useEscapeToClose } from '../utils/a11y';

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/* ─── Sport glyphs: one 32×32 line style, currentColor, identical treatment ─── */
const SPORT_ICONS = {
  tennis: (
    <>
      <circle cx="16" cy="16" r="11" />
      <path d="M6.2 11.2c4.6 1.4 7 4.6 7 9.4 0 2.4-.6 4.6-1.6 6.3" />
      <path d="M25.8 20.8c-4.6-1.4-7-4.6-7-9.4 0-2.4.6-4.6 1.6-6.3" />
    </>
  ),
  padel: (
    <>
      <path d="M16 3.5c5.2 0 8.5 3.6 8.5 8.6 0 5.4-3.6 9.4-6.3 10.6v5.8h-4.4v-5.8C11.1 21.5 7.5 17.5 7.5 12.1c0-5 3.3-8.6 8.5-8.6z" />
      <circle cx="13" cy="10" r=".9" fill="currentColor" /><circle cx="19" cy="10" r=".9" fill="currentColor" />
      <circle cx="16" cy="13.5" r=".9" fill="currentColor" /><circle cx="13" cy="16.5" r=".9" fill="currentColor" />
      <circle cx="19" cy="16.5" r=".9" fill="currentColor" />
    </>
  ),
  squash: (
    <>
      <path d="M20.5 3.5c4 0 6.5 2.6 6 6.6-.6 4.6-4.6 8.8-8.4 9.7-2.6.6-4.4-.6-4.6-3-.4-4.4 2.9-13.3 7-13.3z" />
      <path d="M14.4 18.6 5 28" />
      <path d="M17.7 9.2l5 4.4M16.4 12.6l4.4 3.8M20 6.4l4.8 4.2" opacity=".55" />
      <circle cx="8" cy="9" r="2.3" fill="currentColor" stroke="none" />
    </>
  ),
  badminton: (
    <>
      <path d="M12.3 22.8 6 7.5h20l-6.3 15.3" />
      <path d="M11.4 7.5 13.6 22.8M20.6 7.5 18.4 22.8M16 7.5v15.3" opacity=".55" />
      <path d="M12 22.8h8v1.4a4 4 0 0 1-8 0z" fill="currentColor" />
    </>
  ),
  pickleball: (
    <>
      <rect x="5" y="3.5" width="14" height="16.5" rx="5.5" />
      <path d="M12 20v8" strokeWidth="3.4" />
      <circle cx="23.5" cy="23" r="4.8" />
      <circle cx="22" cy="21.6" r=".8" fill="currentColor" stroke="none" />
      <circle cx="25.2" cy="22.2" r=".8" fill="currentColor" stroke="none" />
      <circle cx="23.4" cy="25" r=".8" fill="currentColor" stroke="none" />
    </>
  ),
  table_tennis: (
    <>
      <circle cx="13.5" cy="12.5" r="8.8" />
      <path d="M19.7 18.7 26.5 25.5" strokeWidth="3.4" />
      <circle cx="25" cy="7" r="2.6" fill="currentColor" stroke="none" />
    </>
  ),
};

/* ─── Layer glyphs ─── */
const LAYER_ICONS = {
  season: (<><rect x="4" y="6" width="24" height="21" rx="4" /><path d="M4 12.5h24M11 3.5v5M21 3.5v5" /><path d="m12 19.5 3 3 6-6" /></>),
  competitions: (<><path d="M10 5h12v6a6 6 0 0 1-12 0z" /><path d="M10 7H6a4 4 0 0 0 4 5M22 7h4a4 4 0 0 1-4 5" /><path d="M16 17v5M11 27h10M12.5 22h7v5h-7z" /></>),
  gamePlans: (<><rect x="6.5" y="5" width="19" height="23" rx="3.5" /><path d="M12 5V3.5h8V5" /><path d="M11 13h10M11 18h10M11 23h6" /></>),
  gameAnalytics: (<><path d="M5 27h22" /><path d="M8.5 22v-5M14 22V11M19.5 22v-7M25 22V6" strokeWidth="3" /></>),
  dataCoach: (<><path d="M5 8.5A3.5 3.5 0 0 1 8.5 5h15A3.5 3.5 0 0 1 27 8.5v10a3.5 3.5 0 0 1-3.5 3.5H14l-6 5v-5h.5" /><path d="m10.5 16 3.2-3.5 3 2.4 4.8-5" /></>),
};

function Glyph({ children, size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

function ExampleBlock({ ex, c }) {
  if (!ex) return null;
  return (
    <div className="zx-ex" style={{ '--c': c }}>
      {ex.q && <div className="zx-ex-q">“{ex.q}”</div>}
      <div className="zx-ex-title">{ex.title}</div>
      <dl>
        {ex.lines.map((l) => (
          <div key={l.k + l.v} className="zx-ex-row">
            <dt>{l.k}</dt>
            <dd>{l.v}{l.tag && <span className="zx-tag">{l.tag}</span>}</dd>
          </div>
        ))}
      </dl>
      {ex.foot && <div className="zx-ex-foot">{ex.foot}</div>}
    </div>
  );
}

function Detail({ layer, sport, c }) {
  const d = layerDetail(layer.key, sport.value);
  return (
    <div className="zx-detail">
      <div className="zx-eyebrow" style={{ color: c }}>{sport.name}</div>
      {d.lead && <p className="zx-lead">{d.lead}</p>}

      {d.groups && (
        <div className={`zx-groups${d.groups.length > 1 ? ' two' : ''}`}>
          {d.groups.map((g) => (
            <div key={g.label}>
              <div className="zx-glabel">{g.label}</div>
              <ul>{g.items.map((it) => <li key={it}>{it}</li>)}</ul>
            </div>
          ))}
        </div>
      )}

      {d.chips && (
        <ul className="zx-chips">{d.chips.map((t) => <li key={t}>{t}</li>)}</ul>
      )}

      {d.quote && <blockquote className="zx-quote" style={{ borderColor: c }}>{d.quote}</blockquote>}

      {(d.example || d.shot) && (
        <div className={`zx-real${d.shot && d.example ? ' split' : ''}`}>
          {(d.example || d.example2) && (
            <div className="zx-exs">
              <ExampleBlock ex={d.example} c={c} />
              <ExampleBlock ex={d.example2} c={c} />
            </div>
          )}
          {d.shot && (
            <figure className="zx-win" style={{ '--c': c }}>
              <div className="zx-win-bar"><i /><i /><i /><span>{d.shot.label}</span></div>
              <img src={d.shot.src} alt={d.shot.alt} width="800" height="612" loading="lazy" decoding="async" />
            </figure>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Cinematic demo player ──────────────────────────────────────────────────
 * A fixed-aspect box (like an embedded video) that plays demoBeats(sport).
 * Nothing inside changes the page height while it runs. A rAF clock drives
 * beat timing; CSS keyframes drive motion, and pausing freezes both
 * (animation-play-state), so progress bar and visuals never drift apart.
 */
function Typed({ text, frac }) {
  const n = Math.round(Math.max(0, Math.min(1, frac)) * text.length);
  return <>{text.slice(0, n)}</>;
}

function Beat({ beat, sport, elapsed, colorOf }) {
  const layer = beat.layer != null ? LAYERS[beat.layer] : null;
  const c = layer ? colorOf(layer.color) : colorOf('accent');
  const ms = (v) => `${Math.round(v)}ms`;
  const eyebrow = layer && <div className="zp-eyebrow" style={{ color: c }}>{layer.num} · {layer.title} · {sport.name}</div>;
  const style = {
    '--c': c,
    animation: `zpIn .7s ${EASE} both, zpOut .38s ease-in ${ms(beat.dur - 380)} forwards`,
  };

  let body = null;
  switch (beat.kind) {
    case 'intro':
      body = (
        <div className="zp-center">
          <div className="zp-eyebrow zp-rise" style={{ color: c }}>{sport.name} · Guided demo</div>
          <div className="zp-h zp-rise" style={{ animationDelay: '150ms' }}>One season.<br /><span style={{ color: c }}>Five layers.</span></div>
          <ol className="zp-menu intro">
            {LAYERS.map((l, i) => (
              <li key={l.key} className="zp-rise" style={{ '--c': colorOf(l.color), animationDelay: ms(1100 + i * 160) }}>
                <span className="zp-mnum">{l.num}</span><span className="zp-mico"><Glyph size={16}>{LAYER_ICONS[l.key]}</Glyph></span>{l.title}
              </li>
            ))}
          </ol>
        </div>
      );
      break;
    case 'title':
      body = (
        <div className="zp-center">
          {/* back to the "menu", pick the next layer up… */}
          <ol className="zp-menu pick">
            {LAYERS.map((l, i) => (
              <li key={l.key} className={i === beat.layer ? 'cur' : i < beat.layer ? 'done' : ''} style={{ '--c': colorOf(l.color) }}>
                <span className="zp-mnum">{l.num}</span><span className="zp-mico"><Glyph size={16}>{LAYER_ICONS[l.key]}</Glyph></span>{l.title}
              </li>
            ))}
          </ol>
          {/* …then its title card */}
          <div className="zp-tcard">
            <div className="zp-tnum zp-rise" style={{ color: c, animationDelay: ms(TITLE_MENU_MS) }}>{layer.num}</div>
            <div className="zp-tico zp-pop" style={{ animationDelay: ms(TITLE_MENU_MS + 120) }}><Glyph size={30}>{LAYER_ICONS[layer.key]}</Glyph></div>
            <div className="zp-h zp-rise" style={{ animationDelay: ms(TITLE_MENU_MS + 220) }}>{layer.title}</div>
            <p className="zp-sub zp-rise" style={{ animationDelay: ms(TITLE_MENU_MS + 1000) }}>{layer.overview}</p>
          </div>
        </div>
      );
      break;
    case 'points':
      body = (
        <div className="zp-pad zp-mid">
          {eyebrow}
          <div className="zp-h2">{beat.label}</div>
          {beat.intro && <p className="zp-lead zp-rise" style={{ animationDelay: '500ms' }}>{beat.intro}</p>}
          <ul className="zp-pts">
            {beat.items.map((it, i) => <li key={it} className="zp-rise" style={{ animationDelay: ms(beat.delays[i]) }}>{it}</li>)}
          </ul>
        </div>
      );
      break;
    case 'chips':
      body = (
        <div className="zp-pad zp-mid">
          {eyebrow}
          <div className="zp-h2">Six ways to look back</div>
          <ul className="zp-chips">
            {beat.items.map((it, i) => <li key={it} className="zp-pop" style={{ animationDelay: ms(beat.delays[i]) }}>{it}</li>)}
          </ul>
          <p className="zp-note zp-rise" style={{ animationDelay: ms(beat.delays[beat.delays.length - 1] + 600) }}>Across seasons, tournaments, leagues, opponents and intensity levels.</p>
        </div>
      );
      break;
    case 'quote':
      body = (
        <div className="zp-pad zp-mid">
          {eyebrow}
          <blockquote className="zp-quote zp-rise" style={{ animationDelay: ms(beat.delays[0]) }}>{beat.quote}</blockquote>
          <div className="zp-note zp-rise" style={{ animationDelay: ms(beat.delays[0] + 900) }}>Data Coach · reads your whole match history</div>
        </div>
      );
      break;
    case 'example':
      body = (
        <div className="zp-pad zp-mid">
          {eyebrow}
          <div className="zp-ex zp-rise" style={{ animationDelay: '250ms' }}>
            <div className="zp-ex-title">{beat.ex.title}</div>
            <dl>
              {beat.ex.lines.map((l, i) => (
                <div key={l.k + l.v} className="zp-ex-row zp-rise" style={{ animationDelay: ms(beat.delays[i]) }}>
                  <dt>{l.k}</dt><dd>{l.v}{l.tag && <span className="zx-tag">{l.tag}</span>}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="zp-note">Real data · test account</div>
        </div>
      );
      break;
    case 'shot': {
      const { crop: [x, y, w, h], hl = [], src, alt, label, caption } = beat.shot;
      body = (
        <div className={`zp-shotwrap${w / h > 2.6 ? ' wide' : ''}`}>
          <figure className="zp-win">
            <div className="zx-win-bar"><i /><i /><i /><span>zenanalytics.io · {label}</span></div>
            <div className="zp-crop" style={{ aspectRatio: `${w} / ${h}` }}>
              <div className="zp-kb" style={{ animationDuration: ms(beat.dur) }}>
                <img src={src} alt={alt} style={{ width: `${(800 / w) * 100}%`, left: `${(-x / w) * 100}%`, top: `${(-y / h) * 100}%` }} />
                {hl.map(([hx, hy, hw, hh], i) => (
                  <span key={i} className="zp-hl zp-pop" style={{
                    left: `${((hx - x) / w) * 100}%`, top: `${((hy - y) / h) * 100}%`,
                    width: `${(hw / w) * 100}%`, height: `${(hh / h) * 100}%`, animationDelay: ms(1100 + i * 350),
                  }} />
                ))}
              </div>
            </div>
          </figure>
          <div className="zp-cap">
            {eyebrow}
            <div className="zp-capt zp-rise" style={{ animationDelay: '700ms' }}>{caption}</div>
            <div className="zp-note zp-rise" style={{ animationDelay: '1300ms' }}>Real app · real data</div>
          </div>
        </div>
      );
      break;
    }
    case 'chat': {
      const { ex, qMs, answerAt, delays } = beat;
      const typedFrac = (elapsed - 700) / qMs;
      const sent = elapsed >= 700 + qMs + 400;
      const thinking = sent && elapsed < answerAt;
      return (
        <div className="zp-beat" style={style}>
          <div className="zp-chat">
            <div className="zx-win-bar"><i /><i /><i /><span>zenanalytics.io · Data Coach</span></div>
            <div className="zp-msgs">
              {sent && <div className="zp-me zp-rise">{ex.q}</div>}
              {sent && (
                <div className="zp-bot">
                  <div className="zp-who"><span className="zp-av" />Data Coach</div>
                  {thinking ? <div className="zp-dots"><i /><i /><i /> reading your matches…</div> : (
                    <div className="zp-bub">
                      <div className="zp-bub-h zp-rise">{ex.title}</div>
                      {ex.lines.map((l, i) => (
                        <p key={l.k} className="zp-rise" style={{ animationDelay: ms(delays[i] - answerAt) }}><b>{l.k}</b> {l.v}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="zp-inp">
              <span className="zp-tx">{!sent && typedFrac > 0 ? <Typed text={ex.q} frac={typedFrac} /> : <span className="zp-ph">Ask about your game…</span>}<span className="zp-caret" /></span>
              <span className="zp-send"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
            </div>
          </div>
        </div>
      );
    }
    case 'outro':
      body = (
        <div className="zp-center">
          <div className="zp-eyebrow zp-rise" style={{ color: c }}>{sport.name} · End of demo</div>
          <div className="zp-h zp-rise" style={{ animationDelay: '150ms' }}>Prepare. Play.<br /><span style={{ color: c }}>Learn from every match.</span></div>
          <p className="zp-sub zp-rise" style={{ animationDelay: '900ms' }}>All five layers are below — open any of them again.</p>
        </div>
      );
      break;
    default:
  }
  return <div className="zp-beat" style={style}>{body}</div>;
}

function DemoPlayer({ sport, colorOf, reduced, onDone }) {
  const beats = useMemo(() => demoBeats(sport.value), [sport.value]);
  const [idx, setIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  // Reduced motion: no forced playback — visitor steps through with Next.
  const [paused, setPaused] = useState(reduced);
  const st = useRef({ idx: 0, elapsed: 0, paused: reduced });
  st.current.paused = paused;

  const go = useCallback((i) => {
    if (i >= beats.length) { onDone(); return; }
    const n = Math.max(0, i);
    st.current.idx = n; st.current.elapsed = 0;
    setIdx(n); setElapsed(0);
  }, [beats.length, onDone]);

  useEffect(() => {
    let raf; let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(100, now - last); last = now;
      const s = st.current;
      if (!s.paused) {
        s.elapsed += dt;
        if (s.elapsed >= beats[s.idx].dur) { go(s.idx + 1); } else setElapsed(s.elapsed);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [beats, go]);

  const beat = beats[idx];
  const cur = beat.layer;
  const fill = (k) => {
    if (beat.kind === 'outro') return 1;
    if (cur == null || k > cur) return 0;
    if (k < cur) return 1;
    const own = beats.filter((b) => b.layer === k);
    const before = own.slice(0, own.indexOf(beat)).reduce((n, b) => n + b.dur, 0);
    return (before + Math.min(elapsed, beat.dur)) / own.reduce((n, b) => n + b.dur, 0);
  };
  const jumpLayer = (k) => go(beats.findIndex((b) => b.layer === k));
  const status = cur != null ? `Layer ${cur + 1} of 5 · ${LAYERS[cur].title}` : beat.kind === 'intro' ? 'Intro' : 'Done';

  return (
    <div className={`zp${reduced ? ' reduced' : ''}`} role="region" aria-roledescription="demo player" aria-label={`${sport.name} guided demo`}>
      <div className="zp-prog">
        {LAYERS.map((l, k) => (
          <button key={l.key} type="button" className={`zp-seg${k === cur ? ' on' : ''}`} style={{ '--c': colorOf(l.color) }}
            onClick={() => jumpLayer(k)} aria-label={`Jump to ${l.num} ${l.title}`} aria-current={k === cur ? 'step' : undefined}>
            <span className="zp-segbar"><span style={{ transform: `scaleX(${fill(k)})` }} /></span>
            <span className="zp-seglbl"><b>{l.num}</b><span className="zp-segname"> {l.title}</span></span>
          </button>
        ))}
      </div>
      <div className={`zp-stage${paused ? ' paused' : ''}`} onClick={() => setPaused((p) => !p)}>
        <Beat key={beat.id} beat={beat} sport={sport} elapsed={reduced ? beat.dur : elapsed} colorOf={colorOf} />
        {paused && !reduced && <div className="zp-pausebadge" aria-hidden="true">Paused · tap to continue</div>}
      </div>
      <div className="zp-ctrl">
        <button type="button" onClick={() => setPaused((p) => !p)} aria-label={paused ? 'Play demo' : 'Pause demo'}>
          {paused
            ? <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M3 1.5v9l7.5-4.5z" fill="currentColor" /></svg>
            : <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 1.5h2.5v9H2.5zM7 1.5h2.5v9H7z" fill="currentColor" /></svg>}
        </button>
        <button type="button" onClick={() => go(idx - 1)} disabled={idx === 0} aria-label="Previous step">
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M7.5 2 3.5 6l4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <button type="button" onClick={() => go(idx + 1)} aria-label="Next step">
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M4.5 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <span className="zp-status" aria-live="polite">{status}</span>
        <button type="button" className="zp-skip" onClick={onDone}>Skip demo</button>
      </div>
    </div>
  );
}

export default function LayeredExplainer({ D }) {
  // No sport is picked at first — the five layer cards stay hidden until a
  // sport is chosen. Picking a sport (or re-tapping its icon later) always
  // (re)plays the cinematic demo for it; afterwards the five cards remain
  // for manual browsing.
  const [activeSport, setActiveSport] = useState(null);
  const [zoomedLayer, setZoomedLayer] = useState(null);
  const [autoplay, setAutoplay] = useState(false);
  const [runId, setRunId] = useState(0);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  // Layers that have been opened stay mounted so collapse can animate and
  // images aren't refetched; unopened layers render no detail (no image cost).
  const [mounted, setMounted] = useState(() => new Set());
  const cardRefs = useRef({});
  const closeRefs = useRef({});
  const lastZoomed = useRef(null);
  const sectionRef = useRef(null);
  const playerFrameRef = useRef(null);

  const sport = SPORTS.find((s) => s.value === activeSport);
  const colorOf = (name) => D[name] || D.accent;

  const reduced = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const open = useCallback((key) => {
    setMounted((m) => (m.has(key) ? m : new Set(m).add(key)));
    setZoomedLayer((z) => (z === key ? null : key));
  }, []);

  // Demo plays whenever a sport icon is tapped, and again from the replay
  // button. Finishing or skipping lands on the five closed cards.
  const startAutoplay = useCallback(() => {
    setZoomedLayer(null);
    setRunId((r) => r + 1);
    setAutoplay(true);
  }, []);

  const endDemo = useCallback(() => {
    const hadFocus = sectionRef.current?.querySelector('.zp')?.contains(document.activeElement);
    setAutoplay(false);
    setHasPlayedOnce(true);
    if (hadFocus) {
      setTimeout(() => cardRefs.current[LAYERS[0].key]?.querySelector('.zx-head')?.focus({ preventScroll: true }), 0);
    }
  }, []);

  const pickSport = useCallback((value) => {
    setActiveSport(value);
    startAutoplay();
    // Bring the player frame fully into view on tap -- without this, a
    // visitor who taps a sport from the top of the row (desktop) or a
    // shorter viewport (mobile) never sees the demo actually start unless
    // they scroll themselves. Feedback 2026-11-03.
    setTimeout(() => {
      const el = playerFrameRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      window.scrollTo({ top: window.scrollY + top - 80, behavior: reduced ? 'auto' : 'smooth' });
    }, 60);
  }, [startAutoplay, reduced]);

  const handleCardTap = open;

  const close = useCallback(() => {
    if (autoplay) { endDemo(); return; }
    setZoomedLayer(null);
  }, [autoplay, endDemo]);
  useEscapeToClose(close);

  useEffect(() => {
    if (zoomedLayer) {
      lastZoomed.current = zoomedLayer;
      const card = cardRefs.current[zoomedLayer];
      closeRefs.current[zoomedLayer]?.focus({ preventScroll: true });
      // Let the expand start, then bring the card's top into view on phones.
      const t = setTimeout(() => {
        if (!card) return;
        const top = card.getBoundingClientRect().top;
        if (top < 70 || top > window.innerHeight * 0.45) {
          window.scrollTo({ top: window.scrollY + top - 80, behavior: reduced ? 'auto' : 'smooth' });
        }
      }, reduced ? 0 : 60);
      return () => clearTimeout(t);
    }
    if (lastZoomed.current) {
      cardRefs.current[lastZoomed.current]?.querySelector('.zx-head')?.focus({ preventScroll: true });
      lastZoomed.current = null;
    }
    return undefined;
  }, [zoomedLayer, reduced]);

  return (
    <section
      ref={sectionRef}
      aria-label="How ZenAnalytics works"
      className="zx"
      style={{
        '--bg0': D.bg0, '--bg1': D.bg1, '--bg2': D.bg2, '--border': D.border,
        '--text': D.text, '--dim': D.dim, '--accent': D.accent,
        fontFamily: D.font,
      }}
    >
      <style>{CSS}</style>
      <div className="zx-inner">
        <div className="zx-sports-label">Choose your sport to start demo</div>
        <div className="zx-sports" role="group" aria-label="Choose your sport">
          {SPORTS.map((s) => {
            const on = s.value === activeSport;
            return (
              <button
                key={s.value}
                type="button"
                className={`zx-sport${on ? ' on' : ''}`}
                aria-pressed={on}
                onClick={() => pickSport(s.value)}
              >
                <Glyph size={34}>{SPORT_ICONS[s.value]}</Glyph>
                <span className="zx-sport-label">{s.name}</span>
              </button>
            );
          })}
        </div>

        {/* The player frame is always on screen, at its own fixed size, even
            before a sport is picked -- so starting the demo plays inside a
            box the visitor already sees, instead of a box suddenly
            appearing (and the page jumping) after their click. */}
        <div ref={playerFrameRef}>
          {sport ? (
            autoplay && <DemoPlayer key={`${sport.value}-${runId}`} sport={sport} colorOf={colorOf} reduced={reduced} onDone={endDemo} />
          ) : (
            <div className="zp zp-idle" aria-hidden="true">
              <div className="zp-stage">
                <p className="zx-prompt">Tap your sport above to start the demo.</p>
              </div>
            </div>
          )}
        </div>

        {sport && hasPlayedOnce && !autoplay && (
          <button type="button" className="zx-replay" onClick={startAutoplay}>
            <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true"><path d="M13.5 8A5.5 5.5 0 1 1 11.8 4M13.5 2v4h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Replay demo
          </button>
        )}

        {sport && !autoplay && (
        <ol className={`zx-layers${zoomedLayer ? ' zoomed' : ''}`}>
          {LAYERS.map((layer) => {
            const c = colorOf(layer.color);
            const isOpen = zoomedLayer === layer.key;
            const dim = zoomedLayer && !isOpen;
            const panelId = `zx-panel-${layer.key}`;
            return (
              <li
                key={layer.key}
                ref={(el) => { cardRefs.current[layer.key] = el; }}
                className={`zx-card${isOpen ? ' open' : ''}${dim ? ' dim' : ''}`}
                style={{ '--c': c }}
              >
                <div className="zx-headrow">
                  <button
                    type="button"
                    className="zx-head"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => handleCardTap(layer.key)}
                  >
                    <span className="zx-icon"><Glyph size={22}>{LAYER_ICONS[layer.key]}</Glyph></span>
                    <span className="zx-htext">
                      <span className="zx-titlerow">
                        <span className="zx-num">{layer.num}</span>
                        <span className="zx-title">{layer.title}</span>
                      </span>
                      <span className="zx-fold"><span className="zx-overview">{layer.overview}</span></span>
                    </span>
                  </button>
                  {isOpen && (
                    <button
                      type="button"
                      className="zx-close"
                      ref={(el) => { closeRefs.current[layer.key] = el; }}
                      onClick={close}
                      aria-label={`Close ${layer.title}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                    </button>
                  )}
                </div>
                <div id={panelId} className="zx-panel" inert={!isOpen} aria-hidden={!isOpen}>
                  <div className="zx-panel-in">
                    {mounted.has(layer.key) && <Detail layer={layer} sport={sport} c={c} />}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
        )}
      </div>
    </section>
  );
}

const EASE = 'cubic-bezier(.22,1,.36,1)';
const TITLE_MENU_SEC = (TITLE_MENU_MS - 350) / 1000; // menu leaves just before the title card lands

const CSS = `
.zx { background: var(--bg0); border-bottom: 1px solid var(--border); padding: clamp(36px, 6vw, 72px) 16px; color: var(--text); }
.zx *, .zx *::before, .zx *::after { box-sizing: border-box; }
.zx-inner { max-width: 760px; margin: 0 auto; }

.zx-sports-label { text-align: center; font: 800 15px ${MONO}; letter-spacing: .08em; text-transform: uppercase; color: var(--text); margin-bottom: 16px; }
.zx-sports { display: flex; flex-wrap: wrap; justify-content: center; gap: clamp(8px, 2.4vw, 14px); margin-bottom: 26px; }
.zx-sport {
  width: 92px; flex: 0 0 auto; border-radius: 16px; display: flex; flex-direction: column; align-items: center; gap: 8px;
  background: var(--bg1); border: 1px solid var(--border); color: rgba(255,255,255,.55); cursor: pointer; padding: 15px 8px 12px;
  transition: color .2s, border-color .2s, background .2s, transform .25s ${EASE}, box-shadow .25s ${EASE};
}
.zx-sport:hover { color: var(--text); border-color: rgba(0,212,255,.4); transform: translateY(-2px); }
.zx-sport.on { color: var(--accent); border-color: var(--accent); background: rgba(0,212,255,.1); transform: translateY(-3px); box-shadow: 0 10px 28px rgba(0,212,255,.22); }
.zx-sport:focus-visible, .zx-head:focus-visible, .zx-close:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.zx-sport-label { font: 700 12px ${MONO}; letter-spacing: .01em; line-height: 1.25; text-align: center; color: inherit; }
@media (max-width: 380px) { .zx-sport { width: 78px; padding: 12px 5px 9px; } .zx-sport-label { font-size: 10.5px; } }

.zx-prompt { text-align: center; color: var(--dim); font-size: 14px; margin: 4px 0 8px; }
.zx-replay {
  display: flex; align-items: center; gap: 7px; margin: 0 auto 18px; padding: 8px 14px; border-radius: 99px;
  background: var(--bg1); border: 1px solid var(--border); color: rgba(255,255,255,.75); cursor: pointer;
  font: 600 12.5px ${MONO}; letter-spacing: .02em; transition: color .2s, border-color .2s, background .2s;
}
.zx-replay:hover { color: var(--accent); border-color: var(--accent); background: rgba(0,212,255,.08); }
.zx-replay:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

.zx-layers { list-style: none; margin: 0; padding: 0; position: relative; display: flex; flex-direction: column; gap: 10px; }
.zx-layers::before { content: ''; position: absolute; left: 33px; top: 24px; bottom: 24px; width: 2px;
  background: linear-gradient(var(--border), rgba(0,212,255,.25), var(--border)); }

.zx-card {
  position: relative; background: var(--bg1); border: 1px solid var(--border); border-radius: 16px;
  transition: opacity .35s ${EASE}, transform .45s ${EASE}, border-color .3s, box-shadow .3s;
  transform-origin: 50% 0;
}
.zx-card::before { content: ''; position: absolute; left: 0; top: 14px; bottom: 14px; width: 3px; border-radius: 0 3px 3px 0; background: var(--c); }
.zx-card.open { border-color: color-mix(in srgb, var(--c) 55%, var(--border)); box-shadow: 0 18px 60px rgba(0,0,0,.4), 0 0 0 1px color-mix(in srgb, var(--c) 25%, transparent); }
.zx-card.dim { opacity: .42; transform: scale(.975); }
.zx-card.dim:hover { opacity: .7; }

.zx-headrow { display: flex; align-items: flex-start; }
.zx-head {
  flex: 1; min-width: 0; display: flex; gap: 14px; align-items: flex-start; text-align: left;
  background: none; border: 0; color: inherit; font: inherit; cursor: pointer; padding: 14px 14px 14px 16px; border-radius: 16px;
}
.zx-icon {
  flex: none; width: 36px; height: 36px; border-radius: 11px; display: grid; place-items: center; color: var(--c);
  background: color-mix(in srgb, var(--c) 14%, var(--bg1)); border: 1px solid color-mix(in srgb, var(--c) 55%, transparent);
}
.zx-htext { min-width: 0; flex: 1; display: block; padding-top: 6px; }
.zx-titlerow { display: flex; align-items: baseline; gap: 10px; }
.zx-num { font: 700 11px ${MONO}; color: var(--c); letter-spacing: .04em; }
.zx-title { font-weight: 800; font-size: 17px; letter-spacing: -.02em; }
.zx-fold { display: grid; grid-template-rows: 1fr; transition: grid-template-rows .4s ${EASE}, opacity .3s; }
.zx-overview { overflow: hidden; min-height: 0; display: block; color: rgba(255,255,255,.72); font-size: 14px; line-height: 1.55; padding-top: 5px; }
.zx-card.dim .zx-fold { grid-template-rows: 0fr; opacity: 0; }
.zx-head:hover .zx-title { color: var(--c); }

.zx-close {
  flex: none; margin: 12px 12px 0 0; width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center;
  background: var(--bg2); border: 1px solid var(--border); color: var(--text); cursor: pointer; padding: 0;
}
.zx-close:hover { border-color: var(--c); color: var(--c); }

.zx-panel { display: grid; grid-template-rows: 0fr; opacity: 0; transition: grid-template-rows .5s ${EASE}, opacity .35s ${EASE}; }
.zx-card.open .zx-panel { grid-template-rows: 1fr; opacity: 1; }
.zx-panel-in { overflow: hidden; min-height: 0; }

.zx-detail { padding: 2px 16px 18px 66px; }
@media (max-width: 560px) { .zx-detail { padding: 2px 14px 16px 16px; } }
.zx-eyebrow { font: 700 10.5px ${MONO}; letter-spacing: .12em; text-transform: uppercase; margin: 6px 0 10px; }
.zx-lead { margin: 0 0 14px; font-size: 15px; line-height: 1.5; font-weight: 600; }
.zx-groups { display: grid; gap: 14px; margin-bottom: 14px; }
.zx-groups.two { grid-template-columns: 1fr 1fr; }
@media (max-width: 560px) { .zx-groups.two { grid-template-columns: 1fr; } }
.zx-glabel { font: 700 10.5px ${MONO}; letter-spacing: .1em; text-transform: uppercase; color: var(--dim); margin-bottom: 6px; }
.zx-groups ul { margin: 0; padding: 0; list-style: none; display: grid; gap: 6px; }
.zx-groups li { position: relative; padding-left: 14px; font-size: 14px; line-height: 1.5; color: rgba(255,255,255,.86); }
.zx-groups li::before { content: ''; position: absolute; left: 0; top: .62em; width: 6px; height: 6px; border-radius: 2px; background: var(--c); }

.zx-chips { list-style: none; margin: 0 0 14px; padding: 0; display: flex; flex-wrap: wrap; gap: 7px; }
.zx-chips li { font: 500 12px ${MONO}; padding: 6px 10px; border-radius: 99px; border: 1px solid color-mix(in srgb, var(--c) 45%, var(--border)); background: color-mix(in srgb, var(--c) 9%, var(--bg1)); }

.zx-quote { margin: 0 0 14px; padding: 12px 14px; border-left: 3px solid; background: var(--bg2); border-radius: 0 10px 10px 0; font-size: 14.5px; line-height: 1.6; color: rgba(255,255,255,.9); }

.zx-real { display: grid; gap: 14px; }
.zx-real.split { grid-template-columns: 1.1fr 1fr; align-items: start; }
@media (max-width: 680px) { .zx-real.split { grid-template-columns: 1fr; } }
.zx-exs { display: grid; gap: 10px; min-width: 0; }
.zx-ex { background: var(--bg0); border: 1px solid var(--border); border-radius: 12px; padding: 12px 13px; min-width: 0; }
.zx-ex-q { font-size: 13.5px; line-height: 1.5; color: rgba(255,255,255,.75); font-style: italic; margin-bottom: 10px; }
.zx-ex-title { font: 700 12px ${MONO}; color: var(--c); margin-bottom: 8px; line-height: 1.45; overflow-wrap: anywhere; }
.zx-ex dl { margin: 0; display: grid; gap: 6px; }
.zx-ex-row { display: grid; grid-template-columns: 74px 1fr; gap: 8px; font-size: 13px; line-height: 1.5; }
.zx-ex dt { font: 600 11px ${MONO}; color: var(--dim); padding-top: 2px; overflow-wrap: anywhere; }
.zx-ex dd { margin: 0; color: rgba(255,255,255,.9); min-width: 0; overflow-wrap: anywhere; }
.zx-tag { margin-left: 8px; font: 600 10px ${MONO}; text-transform: uppercase; letter-spacing: .06em; color: var(--c); border: 1px solid color-mix(in srgb, var(--c) 45%, transparent); padding: 1px 6px; border-radius: 99px; white-space: nowrap; }
.zx-ex-foot { margin-top: 10px; font-size: 12.5px; line-height: 1.5; color: var(--dim); }

.zx-win { margin: 0; border-radius: 12px; overflow: hidden; background: #0b1424; border: 1px solid var(--border);
  box-shadow: 0 20px 50px rgba(0,0,0,.4), 0 0 0 1px color-mix(in srgb, var(--c) 28%, transparent); }
.zx-win-bar { height: 28px; display: flex; align-items: center; gap: 6px; padding: 0 11px; background: var(--bg1); border-bottom: 1px solid var(--border); }
.zx-win-bar i { width: 8px; height: 8px; border-radius: 50%; background: #1f3352; }
.zx-win-bar span { margin-left: 8px; font: 500 11px ${MONO}; color: rgba(255,255,255,.5); }
.zx-win img { display: block; width: 100%; height: auto; }

/* ─── demo player ─── */
.zp { border: 1px solid var(--border); border-radius: 18px; background: var(--bg1); overflow: hidden;
  box-shadow: 0 30px 80px rgba(0,0,0,.35); }
.zp-prog { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; padding: 12px 14px 10px; border-bottom: 1px solid var(--border); }
.zp-seg { background: none; border: 0; padding: 0; cursor: pointer; color: var(--dim); text-align: left; min-width: 0; }
.zp-segbar { display: block; height: 4px; border-radius: 2px; background: var(--bg2); overflow: hidden; }
.zp-segbar > span { display: block; height: 100%; background: var(--c); transform-origin: 0 50%; }
.zp-seglbl { display: block; margin-top: 6px; font: 600 10.5px ${MONO}; letter-spacing: .02em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.zp-seglbl b { color: var(--c); }
.zp-seg.on { color: var(--text); }
.zp-seg:focus-visible, .zp-ctrl button:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 4px; }
@media (max-width: 560px) { .zp-segname { display: none; } .zp-seglbl { text-align: center; } }

.zp-stage { position: relative; aspect-ratio: 16 / 10; overflow: hidden; background: var(--bg0); cursor: pointer; isolation: isolate; }
.zp-idle .zp-stage { cursor: default; display: flex; align-items: center; justify-content: center; }
.zp-idle .zx-prompt { margin: 0; padding: 0 24px; }
.zp-stage::before { content: ''; position: absolute; inset: 0; z-index: -1;
  background-image: linear-gradient(rgba(0,212,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,.05) 1px, transparent 1px);
  background-size: 40px 40px; -webkit-mask-image: radial-gradient(ellipse at center, #000 25%, transparent 75%); mask-image: radial-gradient(ellipse at center, #000 25%, transparent 75%); }
@media (max-width: 600px) { .zp-stage { aspect-ratio: auto; height: 460px; } }
.zp-stage.paused, .zp-stage.paused * { animation-play-state: paused !important; }
.zp-pausebadge { position: absolute; right: 10px; top: 10px; z-index: 2; font: 600 11px ${MONO}; padding: 5px 9px; border-radius: 99px; background: var(--bg1); border: 1px solid var(--border); color: var(--dim); }

.zp-beat { position: absolute; inset: 0; overflow: hidden; }
@keyframes zpIn { from { opacity: 0; transform: scale(.965) translateY(14px); } to { opacity: 1; transform: none; } }
@keyframes zpOut { to { opacity: 0; transform: scale(.975); } }
@keyframes zpRise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
@keyframes zpPop { from { opacity: 0; transform: scale(1.18); } to { opacity: 1; transform: none; } }
@keyframes zpKb { from { transform: scale(1); } to { transform: scale(1.06); } }
@keyframes zpMenuOut { to { opacity: 0; transform: scale(.94); visibility: hidden; } }
@keyframes zpCur { to { opacity: 1; transform: scale(1.06); border-color: var(--c); box-shadow: 0 0 26px color-mix(in srgb, var(--c) 40%, transparent); } }
@keyframes zpDim { to { opacity: .22; transform: scale(.96); } }
@keyframes zpBounce { 0%,100% { transform: none; } 50% { transform: translateY(-5px); } }
@keyframes zpBlink { 50% { opacity: 0; } }
.zp-rise { animation: zpRise .6s ${EASE} both; }
.zp-pop { animation: zpPop .5s cubic-bezier(.34,1.56,.64,1) both; }

.zp-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 24px; }
.zp-pad { position: absolute; inset: 0; padding: clamp(18px, 4.5vw, 36px) clamp(18px, 5vw, 44px); display: flex; flex-direction: column; }
.zp-mid { justify-content: center; }
.zp-eyebrow { font: 700 11px ${MONO}; letter-spacing: .14em; text-transform: uppercase; margin-bottom: 10px; }
.zp-h { font-weight: 800; font-size: clamp(28px, 5.4vw, 44px); letter-spacing: -.035em; line-height: 1.08; }
.zp-h2 { font-weight: 800; font-size: clamp(22px, 4vw, 30px); letter-spacing: -.03em; line-height: 1.15; margin-bottom: 16px; animation: zpRise .6s ${EASE} both .15s; }
.zp-sub { max-width: 520px; margin: 14px auto 0; font-size: clamp(14px, 2vw, 16px); line-height: 1.55; color: var(--dim); }
.zp-lead { margin: 0 0 14px; font-size: clamp(15px, 2.2vw, 18px); font-weight: 600; line-height: 1.45; }
.zp-note { margin-top: 14px; font: 500 11px ${MONO}; letter-spacing: .06em; text-transform: uppercase; color: var(--dim); }

.zp-menu { list-style: none; margin: 0; padding: 0; display: grid; gap: 7px; width: min(320px, 100%); }
.zp-menu li { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 11px; background: var(--bg1);
  border: 1px solid var(--border); font-weight: 700; font-size: 14.5px; text-align: left; }
.zp-mnum { font: 700 11px ${MONO}; color: var(--c); width: 20px; }
.zp-mico { color: var(--c); display: grid; }
.zp-menu.intro { margin-top: 22px; }
.zp-menu.pick { position: absolute; animation: zpMenuOut .35s ease-in ${TITLE_MENU_SEC}s forwards; }
.zp-menu.pick li { animation: zpDim .4s ${EASE} .35s forwards; }
.zp-menu.pick li.done { opacity: .55; }
.zp-menu.pick li.cur { opacity: .7; animation: zpCur .45s ${EASE} .3s forwards; }
.zp-tcard { display: flex; flex-direction: column; align-items: center; }
.zp-tnum { font: 800 clamp(44px, 9vw, 72px) ${MONO}; letter-spacing: -.05em; line-height: 1; }
.zp-tico { margin: 12px 0 10px; width: 56px; height: 56px; border-radius: 16px; display: grid; place-items: center; color: var(--c);
  background: color-mix(in srgb, var(--c) 16%, var(--bg1)); border: 1px solid color-mix(in srgb, var(--c) 60%, transparent); }

.zp-pts { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }
.zp-pts li { position: relative; padding-left: 20px; font-size: clamp(15px, 2.3vw, 18px); line-height: 1.5; }
.zp-pts li::before { content: ''; position: absolute; left: 0; top: .55em; width: 9px; height: 9px; border-radius: 3px; background: var(--c); }
.zp-chips { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 9px; }
.zp-chips li { font: 600 13px ${MONO}; padding: 11px 12px; border-radius: 11px; border: 1px solid color-mix(in srgb, var(--c) 50%, var(--border)); background: color-mix(in srgb, var(--c) 10%, var(--bg1)); }
.zp-quote { margin: 0; padding: 4px 0 4px 18px; border-left: 3px solid var(--c); font-size: clamp(16px, 2.6vw, 21px); line-height: 1.55; font-weight: 500; }

.zp-ex { background: var(--bg1); border: 1px solid var(--border); border-left: 3px solid var(--c); border-radius: 12px; padding: 14px 16px; }
.zp-ex-title { font: 700 12.5px ${MONO}; color: var(--c); margin-bottom: 10px; line-height: 1.45; }
.zp-ex dl { margin: 0; display: grid; gap: 9px; }
.zp-ex-row { display: grid; grid-template-columns: 70px 1fr; gap: 10px; font-size: clamp(13px, 1.9vw, 15px); line-height: 1.5; }
.zp-ex dt { font: 600 11px ${MONO}; color: var(--dim); padding-top: 3px; }
.zp-ex dd { margin: 0; min-width: 0; overflow-wrap: anywhere; }

.zp-shotwrap { position: absolute; inset: 0; display: grid; grid-template-columns: 1.8fr 1fr; gap: 24px; align-items: center; padding: 22px 26px; }
.zp-shotwrap.wide { grid-template-columns: 1fr; align-content: center; gap: 18px; padding: 22px 40px; }
.zp-shotwrap.wide .zp-capt { font-size: clamp(18px, 2.6vw, 23px); }
@media (max-width: 600px) { .zp-shotwrap { grid-template-columns: 1fr; align-content: center; gap: 16px; padding: 18px 16px; } }
.zp-win, .zp-chat { margin: 0; border-radius: 12px; overflow: hidden; background: #0b1424; border: 1px solid var(--border);
  box-shadow: 0 24px 60px rgba(0,0,0,.45), 0 0 0 1px color-mix(in srgb, var(--c) 30%, transparent); }
.zp-win { animation: zpIn .7s ${EASE} both .1s; }
.zp-crop { position: relative; overflow: hidden; }
.zp-kb { position: absolute; inset: 0; animation: zpKb linear both; transform-origin: 50% 50%; }
.zp-kb img { position: absolute; display: block; max-width: none; }
.zp-hl { position: absolute; border: 2px solid var(--c); border-radius: 9px; box-shadow: 0 0 18px var(--c); margin: -4px; padding: 4px; box-sizing: content-box; }
.zp-capt { font-weight: 800; font-size: clamp(19px, 3vw, 26px); letter-spacing: -.025em; line-height: 1.2; }

.zp-chat { position: absolute; inset: clamp(12px, 3vw, 24px); display: flex; flex-direction: column; }
.zp-msgs { flex: 1; min-height: 0; overflow: hidden; padding: 16px; display: flex; flex-direction: column; justify-content: flex-end; gap: 12px; }
@media (max-width: 600px) {
  .zp-chat { inset: 8px; }
  .zp-msgs { padding: 10px; gap: 8px; }
  .zp-me { font-size: 12.5px; padding: 8px 11px; }
  .zp-bub { font-size: 12px; padding: 9px 11px; gap: 4px; line-height: 1.45; }
  .zp-inp { margin: 0 8px 8px; }
}
.zp-me { align-self: flex-end; max-width: 85%; background: color-mix(in srgb, #00d4ff 16%, #0d1829); border: 1px solid rgba(0,212,255,.4);
  border-radius: 16px 16px 4px 16px; padding: 10px 14px; font-size: clamp(13px, 1.9vw, 15px); line-height: 1.45; color: #fff; }
.zp-bot { display: flex; flex-direction: column; gap: 8px; max-width: 94%; }
.zp-who { display: flex; align-items: center; gap: 8px; font: 700 10.5px ${MONO}; letter-spacing: .1em; text-transform: uppercase; color: var(--c); }
.zp-av { width: 20px; height: 20px; border-radius: 7px; background: color-mix(in srgb, var(--c) 20%, transparent); border: 1px solid var(--c); }
.zp-dots { display: flex; align-items: center; gap: 6px; font-size: 13px; color: rgba(255,255,255,.6); }
.zp-dots i { width: 7px; height: 7px; border-radius: 50%; background: var(--c); animation: zpBounce .7s ease-in-out infinite; }
.zp-dots i:nth-child(2) { animation-delay: .12s; } .zp-dots i:nth-child(3) { animation-delay: .24s; }
.zp-bub { background: #0d1829; border: 1px solid var(--border); border-left: 3px solid var(--c); border-radius: 4px 16px 16px 16px; padding: 12px 15px;
  font-size: clamp(12.5px, 1.8vw, 14.5px); line-height: 1.5; color: rgba(255,255,255,.9); display: grid; gap: 6px; }
.zp-bub p { margin: 0; } .zp-bub b { color: var(--c); font-weight: 700; }
.zp-bub-h { font-weight: 800; font-size: 1.12em; color: #fff; }
.zp-inp { margin: 0 12px 12px; height: 44px; border-radius: 12px; border: 1px solid var(--border); background: #0d1829; display: flex; align-items: center; gap: 10px; padding: 0 6px 0 14px; }
.zp-tx { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; display: flex; justify-content: flex-end; font-size: 14px; color: #fff; }
.zp-tx > :first-child { overflow: hidden; text-overflow: clip; }
.zp-ph { color: rgba(255,255,255,.4); margin-right: auto; }
.zp-caret { flex: none; width: 2px; height: 18px; background: var(--c); margin-left: 2px; animation: zpBlink 1s step-end infinite; }
.zp-send { flex: none; width: 32px; height: 32px; border-radius: 9px; background: var(--c); color: #04231a; display: grid; place-items: center; }

.zp-ctrl { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-top: 1px solid var(--border); }
.zp-ctrl button { height: 32px; min-width: 32px; padding: 0 10px; border-radius: 9px; background: var(--bg2); border: 1px solid var(--border); color: var(--text);
  cursor: pointer; display: grid; place-items: center; font: 600 12px ${MONO}; }
.zp-ctrl button:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
.zp-ctrl button:disabled { opacity: .35; cursor: default; }
.zp-status { flex: 1; min-width: 0; font: 600 11px ${MONO}; color: var(--dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: center; }

/* reduced motion: no animation, everything visible; visitor steps with Next */
.zp.reduced .zp-stage *, .zp.reduced .zp-beat { animation: none !important; }
.zp.reduced .zp-menu.pick { display: none; }

@media (prefers-reduced-motion: reduce) {
  .zx-sport, .zx-card, .zx-fold, .zx-panel { transition: opacity .15s linear !important; }
  .zx-card.dim, .zx-sport.on { transform: none; }
}
`;
